import { removerDiacriticos } from '@/shared/utils/text';
import { isCategoriaArbitralTemplate } from '@/types/arbitral/categoriaArbitralTemplate';

import type {
  CertidaoEncerramento,
  CertificadoParaEncerramento,
} from '../types/encerramento.types';

/** O discriminante honesto: a categoria do modelo que gerou o documento. */
const CATEGORIA_DA_CERTIDAO_DE_TRANSITO = 'CERTIDAO_TRANSITO_JULGADO';

/**
 * Fallback de quando a categoria não vem no DTO.
 *
 * A certidão de trânsito é gravada com o mesmo `tipo` (`CERTIFICADO_SENTENCA`) que a intimação de
 * sentença e o emissor genérico — o `tipo` NÃO distingue (decisão registrada no ADR do
 * discriminante). Enquanto o único sinal era o `nome`, este `includes` era o filtro inteiro; hoje
 * ele só cobre a janela entre o deploy do front e o do core, e respostas em cache.
 *
 * A comparação ignora acento: o nome vem de um TEMPLATE editável pela Secretaria, e um título
 * salvo como "Certidao de transito em julgado" — ou com o mesmo acento em NFD, que `includes`
 * não casa com o NFC deste literal — deixaria o grupo vazio sem erro nenhum.
 */
const NOME_DA_CERTIDAO_DE_TRANSITO = removerDiacriticos('certidão de trânsito').toLowerCase();

/** `EM_GERACAO` e `INDISPONIVEL` não existem no back: são derivados de `status` + arquivo. */
function estadoDaCertidao(
  certificado: CertificadoParaEncerramento,
): CertidaoEncerramento['status'] {
  if (certificado.canceladoEm) return 'CANCELADO';
  if (certificado.status === 'PENDENTE') return 'EM_GERACAO';
  if (certificado.status === 'ERRO' || certificado.tamanhoBytes == null) return 'INDISPONIVEL';
  return 'GERADO';
}

function ehCertidaoDeTransito(certificado: CertificadoParaEncerramento): boolean {
  // A categoria só substitui o match por nome quando é RECONHECIDA. Com `if (categoria)`,
  // qualquer string truthy desligava o fallback — e o DTO chega por cast cru
  // (`arbitral-detalhes/store/certificados/services.ts`), então o back mandando uma categoria
  // que este front não conhece esvaziava o grupo Encerramento em silêncio (COR-01 do #986).
  if (isCategoriaArbitralTemplate(certificado.categoria)) {
    return certificado.categoria === CATEGORIA_DA_CERTIDAO_DE_TRANSITO;
  }
  return removerDiacriticos(certificado.nome)
    .toLocaleLowerCase('pt-BR')
    .includes(NOME_DA_CERTIDAO_DE_TRANSITO);
}

export function certidoesDoEncerramento(
  certificados: readonly CertificadoParaEncerramento[],
): CertidaoEncerramento[] {
  return certificados.filter(ehCertidaoDeTransito).map((certificado) => ({
    id: certificado.id,
    nome: certificado.nome,
    emitidaEm: certificado.dataEmissao,
    status: estadoDaCertidao(certificado),
    tamanhoBytes: certificado.tamanhoBytes ?? null,
  }));
}
